'use client'

import { createContext, type ReactNode, useContext } from 'react'

/**
 * The words the code panel shows. The package carries no message catalogs:
 * the admin app reads these English defaults, and the web app provides its
 * translations through `CodeBlockStringsProvider`.
 */
export type CodeBlockStrings = {
  /** The copy button's accessible name, unless `copyLabel` is passed. */
  copyCode: string
  copy: string
  copied: string
  /** Accessible name of the scrolling code region. */
  sampleLabel: (language: string | undefined) => string
}

export const DEFAULT_CODE_BLOCK_STRINGS: CodeBlockStrings = {
  copyCode: 'Copy code',
  copy: 'Copy',
  copied: 'Copied',
  sampleLabel: (language) =>
    language === undefined ? 'Code sample' : `${language} code sample`,
}

const CodeBlockStringsContext = createContext<CodeBlockStrings>(
  DEFAULT_CODE_BLOCK_STRINGS
)

/** Pass a stable (memoized) object: every panel below re-renders when it changes. */
export function CodeBlockStringsProvider({
  strings,
  children,
}: {
  strings: CodeBlockStrings
  children: ReactNode
}) {
  return (
    <CodeBlockStringsContext.Provider value={strings}>
      {children}
    </CodeBlockStringsContext.Provider>
  )
}

/** The panel's words; English outside a provider. */
export function useCodeBlockStrings(): CodeBlockStrings {
  return useContext(CodeBlockStringsContext)
}
